import { createContext, useContext } from 'react';
import { GetActionMap } from 'api/actions/actions';
import createAuthLoginAction from 'api/actions/auth-login/auth-login-action';
import createAuthVerifyTwoFactorLoginAction from 'api/actions/auth-verify-two-factor-login/auth-verify-two-factor-login-action';
import { AuthLoggedUserInfoResponse } from 'api/actions/auth-logged-user-info/auth-logged-user-info-response';

/**
 * Interface of the session stored in local storage.
 */
export interface ISession {
  /**
   * The JWT token.
   */
  jwt: string | null;

  /**
   * The first name of the user.
   */
  firstName: string | null;

  /**
   * The last name of the user.
   */
  lastName: string | null;

  /**
   * The full name of the user.
   */
  fullName: string | null;

  /**
   * The email of the user.
   */
  email: string | null;

  /**
   * The phone number of the user.
   */
  phoneNumber?: string | null;

  /**
   * The user id.
   */
  userId: number | null;

  /**
   * The role of the user.
   */
  role: string | null;

  /**
   * The role id of the user.
   */
  roleId: number | null;

  /** Direct organization of the effective user, if any. */
  organizationId?: number | null;

  /** Effective hierarchy of the direct organization, if supplied by the API. */
  organizationHierarchyId?: number | null;

  /** Viewer-wide department presentation resolved with the authenticated identity. */
  legacyDepartmentUi?: boolean;

  /**
   * The permissions of the user.
   */
  permissions: AuthLoggedUserInfoResponse['permissions'] | null;

  /**
   * The organization name of the user.
   */
  organizationName: string | null;

  /** Whether the direct organization enables Price Offers. */
  priceOffersEnabled: boolean;

  /** Whether the direct organization enables HR. */
  hrEnabled: boolean;

  /** Whether the direct organization enables revisions. */
  revisionsEnabled: boolean;

  /** Whether the direct organization enables forms. */
  formsEnabled: boolean;

  /** Whether the direct organization enables revision templates. */
  revisionTemplatesEnabled: boolean;

  /**
   * Whether the user has two factor authentication enabled.
   */
  twoFactorEnabled: boolean;

  /**
   * Whether the user has a pending two factor setup.
   */
  twoFactorSetupPending: boolean;

  /**
   * Whether platform policy currently requires two factor authentication.
   */
  twoFactorRequired: boolean;

  /**
   * The number of unused two factor recovery codes.
   */
  twoFactorRecoveryCodesRemaining: number;

  /**
   * Whether the user must change the password before using the rest of the app.
   */
  passwordChangeRequired: boolean;

  /**
   * When two factor authentication setup was completed.
   */
  twoFactorSetupCompletedAt?: string | null;
}

/**
 * The impersonate interface.
 */
export type IImpersonate = Omit<ISession, 'jwt'>;

/**
 * The session context interface.
 */
export interface IApiContext extends ISession {
  /**
   * The logout action
   */
  logout: () => void;

  /**
   * The login action.
   */
  login: ReturnType<typeof createAuthLoginAction>;

  /**
   * Completes login for a two factor challenge.
   */
  completeTwoFactorLogin: ReturnType<typeof createAuthVerifyTwoFactorLoginAction>;

  /**
   * Refreshes the current session data from the API.
   */
  refreshSession: () => Promise<AuthLoggedUserInfoResponse>;

  /**
   * Returns the action with the given operationId.
   */
  getAction: GetActionMap;

  /**
   * Whether the API connector is ready.
   */
  ready: boolean;

  /**
   * Whether the session should be resumed.
   */
  shouldResumeSession: boolean;

  /**
   * The impersonated user.
   */
  impersonate?: IImpersonate;

  /**
   * The original session.
   */
  originalSession: Omit<ISession, 'jwt'>;

  /**
   * Sets the impersonated user.
   */
  setImpersonate: (impersonate: IImpersonate | undefined) => void;

  /**
   * Whether the user has the given permission anywhere.
   */
  hasPermissionAnywhere: (permission: string) => boolean;

  /**
   * Whether the user has the given permission globally.
   */
  hasPermissionGlobally: (permission: string) => boolean;
}

/**
 * The session context is used to store the current session.
 *
 * The session context will be provided in <App>.
 */
export const ApiContext = createContext<IApiContext>(undefined!);

/**
 * The use API hook.
 */
export function useApi() {
  return useContext(ApiContext);
}
